import { RefObject, useCallback } from "react";

import { ManualModal } from "@vericus/cadmus-manual";

import { useCurrentUserQuery } from "@/generated/graphql";
import { useAssessmentFragment } from "@/graphql/hooks";

interface Props {
  mountElement: RefObject<HTMLElement>;
  trigger?: JSX.Element;
}

/**
 * Cadmus Manual mounted on a `mountElement`, triggered by a `trigger` element,
 * with access to User and Assessment information for personalised help.
 */
export function ManualWithMutations(props: Props) {
  const { mountElement, trigger } = props;

  const { data: assessmentData } = useAssessmentFragment();
  const { data: userData } = useCurrentUserQuery();

  const assessmentName = assessmentData?.name;
  const subjectName = assessmentData?.subjectName;
  const currentUser = userData?.me;

  // Open Hubspot form with prefilled fields through query params
  const onAskHelp = useCallback(() => {
    const formURL = new URL("https://support.cadmus.io/kb-tickets/new");
    formURL.searchParams.append("requester", "Student");

    if (currentUser) {
      formURL.searchParams.append("firstname", currentUser.givenName);
      formURL.searchParams.append("lastname", currentUser.familyName);
      formURL.searchParams.append("email", currentUser.email ?? "");
    }

    if (assessmentName) {
      formURL.searchParams.append("assessment_name", assessmentName);
    }
    if (subjectName) {
      formURL.searchParams.append("subject_code___name", subjectName);
    }

    window.open(formURL.toString(), "_blank")?.focus?.();
  }, [currentUser, assessmentName, subjectName]);

  // Don't render the manual modal at all when LDB exam is enabled
  if (assessmentData.settings?.examLdbEnabled) {
    return null;
  }

  return (
    <ManualModal
      onAskHelp={onAskHelp}
      mountElement={mountElement}
      trigger={trigger}
    />
  );
}
